import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const DocumentPreview = ({ file, onRemove, onReplace }) => {
  if (!file) return null;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return 'FileImage';
    if (fileType === 'application/pdf') return 'FileText';
    return 'File';
  };

  const isImage = file.type.startsWith('image/');
  const fileUrl = URL.createObjectURL(file);

  return (
    <div className="bg-surface border border-border rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary font-inter">
          Document téléchargé
        </h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            iconName="RotateCcw"
            onClick={onReplace}
            aria-label="Remplacer le document"
          >
            Remplacer
          </Button>
          <Button
            variant="ghost"
            iconName="Trash2"
            onClick={onRemove}
            aria-label="Supprimer le document"
          >
            Supprimer
          </Button>
        </div>
      </div>

      <div className="flex items-start space-x-4">
        {/* File Preview */}
        <div className="flex-shrink-0">
          {isImage ? (
            <div className="w-20 h-20 rounded-lg overflow-hidden border border-border">
              <Image
                src={fileUrl}
                alt="Aperçu du document"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-lg bg-secondary-100 flex items-center justify-center">
              <Icon name={getFileIcon(file.type)} size={32} color="#64748B" />
            </div>
          )}
        </div>

        {/* File Information */}
        <div className="flex-1 min-w-0 space-y-2">
          <div>
            <h4 className="text-sm font-medium text-text-primary font-inter truncate">
              {file.name}
            </h4>
            <p className="text-xs text-text-secondary font-inter">
              {formatFileSize(file.size)} • {file.type.split('/')[1].toUpperCase()}
            </p>
          </div>

          {/* File Status */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <Icon name="CheckCircle" size={16} color="#10B981" />
              <span className="text-sm font-medium text-success font-inter">
                Téléchargé avec succès
              </span>
            </div>
          </div>

          {/* File Details */}
          <div className="grid grid-cols-2 gap-4 pt-2 text-xs text-text-secondary font-inter">
            <div>
              <span className="block font-medium">Type:</span>
              <span>{file.type}</span>
            </div>
            <div>
              <span className="block font-medium">Modifié:</span>
              <span>{new Date(file.lastModified).toLocaleDateString('fr-FR')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-accent-50 rounded-lg p-3 border border-accent-100">
        <div className="flex items-start space-x-2">
          <Icon name="Shield" size={16} color="#059669" className="flex-shrink-0 mt-0.5" />
          <div className="text-xs text-accent-600 font-inter">
            <span className="font-medium">Sécurisé:</span> Votre document est chiffré et protégé selon les normes RGPD.
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-border-light">
        <div className="flex items-center space-x-4 text-xs text-text-secondary font-inter">
          <span className="flex items-center space-x-1">
            <Icon name="Eye" size={14} />
            <span>Prévisualisation disponible</span>
          </span>
          {isImage && (
            <span className="flex items-center space-x-1">
              <Icon name="ZoomIn" size={14} />
              <span>Cliquez pour agrandir</span>
            </span>
          )}
        </div>
        
        <Button
          variant="text"
          iconName="ExternalLink"
          iconPosition="right"
          onClick={() => window.open(fileUrl, '_blank')}
          className="text-xs"
        >
          Ouvrir
        </Button>
      </div>
    </div>
  );
};

export default DocumentPreview;